import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { SSEState } from '@/lib/sse'
import { combineLiveStates } from '@/lib/sse'

type Source = { id: number; state: SSEState }

type LiveStatusContextValue = {
  state: SSEState
  register: () => number
  update: (id: number, state: SSEState) => void
  release: (id: number) => void
}

const LiveStatusContext = createContext<LiveStatusContextValue | null>(null)

export function LiveStatusProvider({ children }: { children: ReactNode }) {
  const sourcesRef = useRef<Map<number, Source>>(new Map())
  const nextIdRef = useRef(1)
  const [state, setState] = useState<SSEState>('closed')

  const recompute = useCallback(() => {
    if (sourcesRef.current.size === 0) {
      setState('closed')
      return
    }
    const next = combineLiveStates(
      ...Array.from(sourcesRef.current.values()).map((s) => s.state),
    )
    setState(next)
  }, [])

  const register = useCallback(() => {
    const id = nextIdRef.current++
    sourcesRef.current.set(id, { id, state: 'connecting' })
    recompute()
    return id
  }, [recompute])

  const update = useCallback(
    (id: number, next: SSEState) => {
      const existing = sourcesRef.current.get(id)
      if (!existing) return
      existing.state = next
      recompute()
    },
    [recompute],
  )

  const release = useCallback(
    (id: number) => {
      sourcesRef.current.delete(id)
      recompute()
    },
    [recompute],
  )

  const value = useMemo<LiveStatusContextValue>(
    () => ({ state, register, update, release }),
    [state, register, update, release],
  )

  return (
    <LiveStatusContext.Provider value={value}>
      {children}
    </LiveStatusContext.Provider>
  )
}

export function useLiveStatus(): SSEState {
  const ctx = useContext(LiveStatusContext)
  if (!ctx) return 'closed'
  return ctx.state
}

export function useReportLiveState(state: SSEState | undefined): void {
  const ctx = useContext(LiveStatusContext)
  const idRef = useRef<number | null>(null)

  useEffect(() => {
    if (!ctx) return
    const id = ctx.register()
    idRef.current = id
    return () => {
      ctx.release(id)
      idRef.current = null
    }
  }, [ctx])

  useEffect(() => {
    if (!ctx || idRef.current == null || !state) return
    ctx.update(idRef.current, state)
  }, [ctx, state])
}
